import * as React from 'preact/compat'
import { SvelteComponent } from "svelte";
import { HttpError } from '../api'
import type { ComponentProps, ComponentType } from 'preact'
import type { ComponentConstructorOptions } from 'svelte'

declare global {
  interface Window {
    sgDomain?: string,
    log?: (tag: string, arg: any, ...args: any[]) => void,
    trace?: (tag: string, arg: any, ...args: any[]) => void,
    warn?: (tag: string, arg: any, ...args: any[]) => void,
    error?: (tag: string, arg: any, ...args: any[]) => void,
    info?: (tag: string, arg: any, ...args: any[]) => void,
    showModal?: typeof showModal
  }

  interface HTMLElement {
    registeredHandlers?: { [key: string]: boolean }
  }
}

export function sgVersion() {
  return process.env.RELEASE_VERSION
}

export const ready = (callback: () => void) => {
  if (document.readyState != "loading") {
    callback()
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
}

export const wait = (timer: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, timer)
  })
}

export const disableScroll = () => {
  if (document.documentElement.classList.contains('no-scroll')) {
    return false
  }

  if (window.innerWidth > document.body.clientWidth) {
    document.documentElement.classList.add('no-scroll--scrollbar-fix')
  }
  document.documentElement.classList.add('no-scroll')
  return true
}

export const enableScroll = () => {
  document.documentElement.classList.remove('no-scroll')
  document.documentElement.classList.remove('no-scroll--scrollbar-fix')
}

export const disableTabIndex = (element: HTMLElement) => {
  const dis = (el: HTMLElement) => {
    el.tabIndex = -1
  }
  element.querySelectorAll('a').forEach(dis)
  element.querySelectorAll('button').forEach(dis)
}

export const enableTabIndex = (element: HTMLElement) => {
  element.querySelectorAll('[tabindex="-1"]').forEach(el => {
    el.removeAttribute('tabindex');
  })
}

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function loadStylesheet(name: string) {
  return new Promise<void>((resolve) => {
    const domain = window.sgDomain || ''
    const link = `${domain}/dist/css/${name}.css?${sgVersion()}`
    if (document.querySelector(`[data-cssname="${name}"]`) || document.querySelector(`link[href="${link}"]`)) {
      resolve()
      return
    }
    const style = document.createElement('link')
    style.rel = 'stylesheet'
    style.href = link
    style.setAttribute('data-cssname', name)
    style.onload = () => resolve()
    style.onerror = () => resolve()
    document.head.append(style)
  })
}

export function throttle(func: Function, wait: number, options: {
  leading?: boolean,
  trailing?: boolean
} = {}) {
  let context: () => void
  let args: IArguments
  let result: any
  let timeout = null
  let previous = 0
  if (!options) options = {}
  const later = function () {
    previous = options.leading === false ? 0 : Date.now()
    timeout = null
    result = func.apply(context, args)
    // noinspection PointlessBooleanExpressionJS
    if (!timeout) context = args = null
  }
  return function () {
    const now = Date.now()
    if (!previous && options.leading === false) previous = now
    const remaining = wait - (now - previous)
    context = this
    args = arguments
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      previous = now
      result = func.apply(context, args)
      if (!timeout) context = args = null
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining)
    }
    return result
  }
}

export function getRelativeTimestamp(date: Date) {
  const currentDate = new Date()

  if (date.getFullYear() === currentDate.getFullYear()) {
    if (date.getMonth() === currentDate.getMonth() && date.getDate() === currentDate.getDate()) {
      return 'Сегодня, ' + date.toLocaleString('ru-RU', {
        hour: 'numeric',
        minute: '2-digit'
      })
    }

    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function captureException(tag: string, e: Error | string) {
  error(tag, e)
}

export function captureFetchException(e: Error) {
  if (e.message === 'Failed to fetch' || e instanceof HttpError) {
    return
  }
  captureException('Fetch', e)
}

export function numString(number: number, [one, two, five]: [string, string, string]) {
  if (number % 100 > 10 && number % 100 < 20) {
    return five
  }
  if (number % 10 === 1) {
    return one
  }
  if (number % 10 >= 2 && number % 10 <= 4) {
    return two
  }
  return five
}

function loggingFunction(method: 'log' | 'trace' | 'warn' | 'error' | 'info', tag: string, ...args: any[]) {
  const style = 'color: inherit;'
  const argsToPass = []
  let hue = 0
  for (let i = 0; i < tag.length; i++) {
    hue += tag.charCodeAt(i)
  }
  while (hue > 360) {
    hue -= 360
  }
  const labelStyle = `color: white;
      display: inline-block;
      font-family: Roboto, sans-serif;
      font-size: 12px;
      line-height: 1em;
      background-color: oklch(36.9% 0.058 ${hue});
      padding: 4px 8px 4px 28px;
      border-radius: 4px;
      margin: 0 5px 0 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 43 38' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M32 0H18c-2.2.4-2.4 2.9-2.7 4.6h18.1l.5-3c0-.9-1-1.6-1.9-1.6zM21.4 10H15c-.5-.2-.8.3-.9.7l-1 5.7 20 .1c1.4 0 2.5 1.5 2.2 3L34 28h4c1.3-.1 2.2-1.4 2.4-2.6l2.3-13c.1-.7.2-1.5-.4-2-.5-.6-1.3-.6-2-.6h-19zM6 10H4.9c-1.3 0-2.3 1.3-2.5 2.5L0 26c-.2 1.1.8 2.2 2 2 8.5.1 17 0 25.6 0 .4.1 1 0 1-.5.5-1.9.7-3.8 1.1-5.7H11.8c-1.2-.1-2.5.2-3.5-.6-.8-.6-1-1.8-.8-2.8L9.3 10H6zm12.9 23.4h-9c-.2.9-.4 1.8-.4 2.7.2.9 1 1.5 2 1.4h12.8c.8 0 1.7 0 2.4-.6 1-.9 1-2.3 1.2-3.5h-9z' fill='%23ec2227'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-size: 16px 16px;
      background-position: 8px center;
    `

  argsToPass.unshift(labelStyle, style)
  console[method](`%c${tag}%c`, ...argsToPass, ...args)
}

export function log(tag: string, ...args: any[]) {
  loggingFunction('log', tag, ...args)
}

export function trace(tag: string, ...args: any[]) {
  loggingFunction('trace', tag, ...args)
}

export function warn(tag: string, ...args: any[]) {
  loggingFunction('warn', tag, ...args)
}

export function error(tag: string, ...args: any[]) {
  loggingFunction('error', tag, ...args)
}

export function info(tag: string, ...args: any[]) {
  loggingFunction('info', tag, ...args)
}

if (typeof window !== 'undefined' && !('log' in window)) {
  window.log = log
  window.trace = trace
  window.warn = warn
  window.error = error
  window.info = info
}

export function gameDateToInt(date: string): number {
  let parts = date.split('-')
  if (parts[0] === '0000' || parts[0] === '0001') {
    parts[0] = '9999'
  }
  if (parts[1] === '00') {
    parts[1] = '01'
  }
  if (parts[2] === '00') {
    parts[2] = '01'
  }
  return new Date(parts.join('-')).getTime() / 1000
}

// Тип конструктора Svelte компонента
type SvelteComponentConstructor<Props extends Record<string, any> = any> = new (
  options: ComponentConstructorOptions<Props>
) => SvelteComponent<Props>

function isSvelteComponentConstructor(
  Component: any
): Component is SvelteComponentConstructor {
  return Component.$destroy || Component.prototype?.$destroy || Component.prototype?.prototype?.$destroy
}

interface ModalComponentInterface {
  onClose: () => void
}

// Перегрузка для Preact компонентов
export function showModal<C extends ComponentType<ModalComponentInterface>>(
  Component: C,
  props?: Omit<ComponentProps<C>, 'onClose'>,
  onClose?: () => void,
  append?: boolean
): void

// Перегрузка для Svelte компонентов
export function showModal<P extends ModalComponentInterface>(
  Component: SvelteComponentConstructor<P>,
  props?: Omit<P, 'onClose'>,
  onClose?: () => void,
  append?: boolean
): void

// Реализация
export function showModal(
  Component: any,
  props: Record<string, any> = {},
  onClose?: () => void,
  append?: boolean
): void {
  const div = document.createElement('div')
  if (append) {
    document.body.append(div)
  }

  const isSvelte = isSvelteComponentConstructor(Component)
  let instance: { $destroy: () => void; } | undefined;

  const newProps = {
    ...props,
    onClose: () => {
      if (!isSvelte) {
        React.unmountComponentAtNode(div)
      } else {
        instance?.$destroy()
      }
      div.remove()
      if (onClose && typeof onClose === 'function') {
        onClose()
      }
    }
  }

  if (isSvelte) {
    instance = new Component({
      target: div,
      props: newProps,
    })
    return
  }

  React.render(React.createElement(Component as ComponentType<any>, newProps), div)
}

if (typeof window !== 'undefined') {
  window.showModal = showModal;
} else if (typeof globalThis !== 'undefined') {
  globalThis.showModal = showModal;
} else {
  throw 'WTF?!'
}

export function createButtonPlugin<ElementType extends HTMLElement>(name: string, callback: (element: ElementType) => void) {
  return (elements: NodeListOf<ElementType>) => {
    elements.forEach(element => {
      if (typeof element.registeredHandlers !== 'undefined' && element.registeredHandlers[name]) {
        return
      }
      if (typeof element.registeredHandlers === 'undefined') {
        element.registeredHandlers = {}
      }
      element.registeredHandlers[name] = true
      element.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        callback(element)
      })
    })
  }
}

export function supportsSelector(selector: string) {
  const style = document.createElement('style')
  document.head.appendChild(style)
  try {
    style.sheet.insertRule(selector + '{}', 0)
  } catch (e) {
    return false
  } finally {
    document.head.removeChild(style)
  }
  return true
}

export function insertPJAXHead(head: string) {
  const content = document.createElement('div')
  content.innerHTML = head
  Array.from(content.children).forEach(node => {
    if (node.tagName === 'SCRIPT') {
      const existingNode = document.querySelector(`script[src="${node.getAttribute('src')}"]`)
      if (existingNode) {
        return
      }
    }
    if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet' && node.getAttribute('href')) {
      const existingNode = document.querySelector(`link[href="${node.getAttribute('href')}"]`)
      if (existingNode) {
        return
      }
    }
    document.head.append(node)
  })
}

const registeredElements: { [key: string]: boolean } = {}

export function registerCustomElement(name: string, file: string) {
  const register = async () => {
    if (registeredElements[name]) {
      return
    }
    if (document.querySelector(name)) {
      try {
        (await import(`${file}?${sgVersion()}`)).default()
        registeredElements[name] = true
      } catch (e) {
        // Do nothing
      }
    }
  }
  // noinspection JSIgnoredPromiseFromCall
  register()
  new MutationObserver(register).observe(document, {
    childList: true,
    subtree: true
  })
}

export function deepFind(obj: object, path: string): any {
  const paths = path.split('.')
  let current = obj
  let i: number;

  for (i = 0; i < paths.length; ++i) {
    if (current[paths[i]] == undefined) {
      return undefined;
    } else {
      current = current[paths[i]];
    }
  }
  return current;
}

/**
 * Проверяет, является ли файл изображением по заголовкам файла (magic bytes)
 * @param file - файл для проверки
 * @returns Promise<boolean> - true если файл является изображением
 */
export function isImageByHeaders(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer
      const uint8Array = new Uint8Array(arrayBuffer)

      // Проверяем magic bytes для различных форматов изображений

      // JPEG: FF D8 FF
      if (uint8Array.length >= 3 &&
        uint8Array[0] === 0xFF &&
        uint8Array[1] === 0xD8 &&
        uint8Array[2] === 0xFF) {
        resolve(true)
        return
      }

      // PNG: 89 50 4E 47 0D 0A 1A 0A
      if (uint8Array.length >= 8 &&
        uint8Array[0] === 0x89 &&
        uint8Array[1] === 0x50 &&
        uint8Array[2] === 0x4E &&
        uint8Array[3] === 0x47 &&
        uint8Array[4] === 0x0D &&
        uint8Array[5] === 0x0A &&
        uint8Array[6] === 0x1A &&
        uint8Array[7] === 0x0A) {
        resolve(true)
        return
      }

      // GIF: 47 49 46 38 (GIF8) или 47 49 46 39 (GIF9)
      if (uint8Array.length >= 4 &&
        uint8Array[0] === 0x47 &&
        uint8Array[1] === 0x49 &&
        uint8Array[2] === 0x46 &&
        (uint8Array[3] === 0x38 || uint8Array[3] === 0x39)) {
        resolve(true)
        return
      }

      // WebP: 52 49 46 46 ... 57 45 42 50
      if (uint8Array.length >= 12 &&
        uint8Array[0] === 0x52 &&
        uint8Array[1] === 0x49 &&
        uint8Array[2] === 0x46 &&
        uint8Array[3] === 0x46 &&
        uint8Array[8] === 0x57 &&
        uint8Array[9] === 0x45 &&
        uint8Array[10] === 0x42 &&
        uint8Array[11] === 0x50) {
        resolve(true)
        return
      }

      // BMP: 42 4D
      if (uint8Array.length >= 2 &&
        uint8Array[0] === 0x42 &&
        uint8Array[1] === 0x4D) {
        resolve(true)
        return
      }

      // Если ни один формат не подошел
      resolve(false)
    }

    reader.onerror = () => resolve(false)

    // Читаем первые 16 байт файла (достаточно для проверки всех форматов)
    reader.readAsArrayBuffer(file.slice(0, 16))
  })
}
